// src/pages/portal/PortalRefundsPage.js
//
// The refunds half of the audit surface, sitting beside Transactions and
// Voids in the same link tabs and behind the same `portal:voids`
// permission — money and orders taken back are one story.
//
// Refunds are deliberately NOT Void rows. A Void asserts a record was
// deleted; a refunded sale is still standing, and writing one would make
// the sale look deleted to everything reading that table.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import PortalLayout, { NAV_FEATURE_MAP, NAV_PERMISSION_MAP } from './PortalLayout';
import { refundService } from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import useIsMobile from '../../hooks/useIsMobile';
import MobileCard from '../../components/common/MobileCard';
import PortalLinkTabs, { buildTransactionsVoidsTabs } from '../../components/common/PortalLinkTabs';
import { C } from '../../styles/theme';

const FONT = "'Plus Jakarta Sans', sans-serif";

const OUTCOME_LABELS = {
    returned: 'refund_outcome_returned',
    kept_by_customer: 'refund_outcome_kept_by_customer',
    binned: 'refund_outcome_binned',
};

const Badge = ({ children, color, bg }) => (
    <span style={{
        fontSize: 11, fontWeight: 700, letterSpacing: 0.4, borderRadius: 5,
        padding: '2px 8px', background: bg, color, display: 'inline-block',
        fontFamily: FONT,
    }}>
        {children}
    </span>
);

const DetailRow = ({ label, value }) => (
    <div style={{
        display: 'flex', justifyContent: 'space-between', gap: 12,
        padding: '4px 0', fontFamily: FONT,
    }}>
        <span style={{ color: C.muted }}>{label}</span>
        <span style={{ fontWeight: 600, textAlign: 'right' }}>{value}</span>
    </div>
);

/** "2026-07-31 02:47:56" → "31 Jul 2026 · 02:47". The time is shown, not
 *  hidden: it is what distinguishes two business days opened on one date. */
const businessDayLabel = (value) => {
    const [datePart, timePart = ''] = String(value || '').split(' ');
    const parsed = new Date(`${datePart}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return value;
    return `${format(parsed, 'd MMM yyyy')}${timePart ? ` · ${timePart.slice(0, 5)}` : ''}`;
};

const itemsSummary = (refund) => (refund.items || [])
    .map((i) => `${i.quantity}× ${i.product_name}`)
    .join(', ');

export default function PortalRefundsPage() {
    const { t } = useLanguage();
    const { getCurrency, hasPermission, hasFeature } = useAuth();
    const isMobile = useIsMobile();

    const currency = getCurrency();
    const linkTabs = buildTransactionsVoidsTabs({
        t, hasPermission, hasFeature,
        navFeatureMap: NAV_FEATURE_MAP, navPermissionMap: NAV_PERMISSION_MAP,
    });

    const [refunds, setRefunds] = useState([]);
    const [loading, setLoading] = useState(true);
    // The FULL business-day string, e.g. "2026-07-31 02:47:56". NOT a
    // calendar date: a tenant can open two business days on one date, and
    // querying by date would merge their takings. A date picker cannot
    // produce these values, which is why this is a list of real days.
    const [businessDays, setBusinessDays] = useState([]);
    const [salesDate, setSalesDate] = useState(null);

    useEffect(() => {
        let cancelled = false;
        refundService.businessDays()
            .then((days) => {
                if (cancelled) return;
                const list = Array.isArray(days) ? days : [];
                setBusinessDays(list);
                setSalesDate(list[0] || null);
                if (list.length === 0) setLoading(false);
            })
            .catch(() => {
                if (cancelled) return;
                setBusinessDays([]);
                setLoading(false);
            });
        return () => { cancelled = true; };
    }, []);

    const load = useCallback(async () => {
        if (!salesDate) return;
        setLoading(true);
        try {
            setRefunds(await refundService.forSalesDate(salesDate) || []);
        } catch {
            setRefunds([]);
        } finally {
            setLoading(false);
        }
    }, [salesDate]);

    useEffect(() => { load(); }, [load]);

    const total = useMemo(
        () => refunds.reduce((sum, r) => sum + Number(r.amount || 0), 0),
        [refunds],
    );

    const money = (n) => `${currency} ${Number(n || 0).toFixed(2)}`.trim();

    const columns = [
        t('refund_sale'), t('refund_amount'), t('payment_method'),
        t('refund_items_returned'), t('refund_items_outcome'), t('refund_reason'),
        t('refund_requested_by'), t('refund_approved_by'),
    ];

    return (
        <PortalLayout>
            <div style={{ fontFamily: FONT, color: C.text }}>
                <PortalLinkTabs tabs={linkTabs} active="refunds" />

                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    marginBottom: 20, flexWrap: 'wrap', gap: 12,
                }}>
                    <div>
                        <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, color: C.text }}>
                            {t('refunds_title')}
                        </h1>
                        <p style={{ fontSize: 13, color: C.muted, margin: '4px 0 0' }}>
                            {t('refunds_subtitle')}
                        </p>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{
                            fontSize: 11, fontWeight: 700, color: C.muted,
                            textTransform: 'uppercase', letterSpacing: 0.8,
                        }}>
                            {t('business_day')}
                        </span>
                        <select
                            value={salesDate || ''}
                            onChange={(e) => setSalesDate(e.target.value)}
                            disabled={businessDays.length === 0}
                            style={{
                                minHeight: 38, borderRadius: 8, padding: '0 10px',
                                border: `1.5px solid ${C.border}`, fontFamily: FONT,
                                fontSize: 13, background: C.white, color: C.text,
                            }}
                        >
                            {businessDays.length === 0 && (
                                <option value="">{t('refunds_no_business_days')}</option>
                            )}
                            {businessDays.map((day) => (
                                <option key={day} value={day}>{businessDayLabel(day)}</option>
                            ))}
                        </select>
                    </label>
                </div>

                {!loading && refunds.length > 0 && (
                    <div style={{
                        padding: '14px 16px', borderRadius: 10, marginBottom: 20,
                        background: C.white, border: `1.5px solid ${C.border}`,
                        display: 'inline-block', minWidth: 180,
                    }}>
                        <div style={{
                            fontSize: 11, fontWeight: 700, color: C.muted,
                            textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4,
                        }}>
                            {t('refunds_total')}
                        </div>
                        <div style={{ fontSize: 20, fontWeight: 800, color: C.red }}>
                            {money(total)}
                        </div>
                    </div>
                )}

                {loading && (
                    <div style={{ padding: 24, color: C.muted }}>{t('loading')}</div>
                )}

                {!loading && refunds.length === 0 && (
                    <div style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                        justifyContent: 'center', padding: '48px 24px', color: C.muted,
                    }}>
                        <div style={{ marginBottom: 12, opacity: 0.4 }}>
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                strokeLinejoin="round">
                                <path d="M3 12a9 9 0 1 0 3-6.7" />
                                <polyline points="3 4 3 10 9 10" />
                            </svg>
                        </div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>
                            {t('refunds_empty_title')}
                        </div>
                        <div style={{ fontSize: 13, marginTop: 4 }}>
                            {t('refunds_empty_subtitle')}
                        </div>
                    </div>
                )}

                {!loading && refunds.length > 0 && (isMobile ? (
                    <div>
                        {refunds.map((r) => (
                            <MobileCard
                                key={r.refund_id}
                                accent="red"
                                title={r.transaction_id}
                                subtitle={r.reason}
                                amount={money(r.amount)}
                                status={{ label: r.payment_method, tone: 'red' }}
                            >
                                <div style={{ fontSize: 13 }}>
                                    <DetailRow label={t('refund_items_returned')}
                                        value={itemsSummary(r) || '—'} />
                                    <DetailRow label={t('refund_items_outcome')}
                                        value={r.items_outcome
                                            ? t(OUTCOME_LABELS[r.items_outcome]) : '—'} />
                                    <DetailRow label={t('refund_requested_by')}
                                        value={r.requested_by_name || '—'} />
                                    <DetailRow label={t('refund_approved_by')}
                                        value={r.approved_by_name || '—'} />
                                </div>
                            </MobileCard>
                        ))}
                    </div>
                ) : (
                    <div style={{
                        background: C.white, border: `1.5px solid ${C.border}`,
                        borderRadius: 10, overflowX: 'auto',
                    }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead>
                                <tr style={{ background: C.bg }}>
                                    {columns.map((label) => (
                                        <th key={label} style={{
                                            textAlign: 'left', padding: '10px 12px',
                                            fontSize: 11, fontWeight: 700, color: C.muted,
                                            textTransform: 'uppercase', letterSpacing: 0.8,
                                            whiteSpace: 'nowrap',
                                        }}>
                                            {label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {refunds.map((r) => (
                                    <tr
                                        key={r.refund_id}
                                        style={{ borderTop: `1px solid ${C.border}` }}
                                        onMouseEnter={(e) => { e.currentTarget.style.background = C.bg; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                                    >
                                        <td style={{ padding: '10px 12px', fontWeight: 700 }}>
                                            {r.transaction_id}
                                        </td>
                                        <td style={{ padding: '10px 12px', fontWeight: 800, color: C.red }}>
                                            {money(r.amount)}
                                        </td>
                                        <td style={{ padding: '10px 12px' }}>{r.payment_method}</td>
                                        <td style={{ padding: '10px 12px' }}>{itemsSummary(r) || '—'}</td>
                                        <td style={{ padding: '10px 12px' }}>
                                            {r.items_outcome
                                                ? <Badge color={C.amber} bg={C.amberLight}>
                                                    {t(OUTCOME_LABELS[r.items_outcome])}
                                                </Badge>
                                                : '—'}
                                        </td>
                                        <td style={{ padding: '10px 12px', color: C.muted }}>{r.reason}</td>
                                        <td style={{ padding: '10px 12px' }}>{r.requested_by_name || '—'}</td>
                                        <td style={{ padding: '10px 12px' }}>{r.approved_by_name || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ))}
            </div>
        </PortalLayout>
    );
}
